import { useFilteredEntryQuery } from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import { DrilldownRolodexType } from 'features/drilldown/rolodex/ontology/ontologyTypes'
import { RolodexEntryDispenserType, useRolodexEntries } from 'features/rolodex'
import { useEffect, useMemo, useState } from 'react'

// Remove the 'get' functions from the dispenser so we can use the array type
type FilteredRolodexEntryDispenserType = {
  [K in keyof RolodexEntryDispenserType as K extends `get${string}`
    ? never
    : K]: RolodexEntryDispenserType[K]
}
interface UseFilteredEntriesOptions<K extends keyof RolodexEntryDispenserType> {
  entryType: DrilldownRolodexType
  dataKey: K
  limit?: number
}

/**
 * This hook is used to retrieve entries from rolodex based on a type and the filters provided via context.
 *
 * The core utility of this class is that it avoids returning an empty entry array while loading (eg upon filter changes)
 * which is what useRolodexEntries itself returns while loading (ie if buffering is not used).
 *
 * TODO: try to figure out a way to make this work without consumers having to supply the data key -
 * this is prohibitive in cases where generic types are being used - eg where the user selects the type
 *
 * @param entryType - The type of entry to retrieve
 * @param dataKey - The key of the data to retrieve from the rolodex result
 * @param limit - The number of entries to retrieve - can be undefined for no limit and the hook works fine.
 * @returns The previous data, the active data, and the loading state
 */
export function useBufferedFilteredEntries<
  K extends keyof Omit<FilteredRolodexEntryDispenserType, 'duckDbFeatureFlag'>,
>({
  entryType,
  dataKey,
  limit,
}: UseFilteredEntriesOptions<K>): {
  data: FilteredRolodexEntryDispenserType[K]
  isLoading: boolean
} {
  const [bufferedEntries, setBufferedEntries] = useState<
    FilteredRolodexEntryDispenserType[K]
  >([])
  const { query, isLoading: isLoadingQuery } = useFilteredEntryQuery(entryType)

  const rolodexResult = useRolodexEntries(
    useMemo(() => (query ? [{ ...query, limit }] : []), [query, limit])
  )

  const isLoading = isLoadingQuery || rolodexResult.isLoading

  const activeData = rolodexResult[dataKey] as RolodexEntryDispenserType[K]

  // Update the data [that which gets returned] only when loading completes
  useEffect(() => {
    if (!isLoading && activeData) {
      setBufferedEntries(activeData)
    }
  }, [activeData, isLoading])

  return {
    data: bufferedEntries,
    isLoading,
  }
}

// We should generally prefer the useBufferedFilteredEntries hook instead of this one.
export function useFilteredEntries(type: DrilldownRolodexType, limit?: number) {
  const { query } = useFilteredEntryQuery(type)
  return useRolodexEntries(
    useMemo(() => (query ? [{ ...query, limit }] : []), [query, limit])
  )
}
